import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/api/client';

export type AsyncState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: ApiError };

export interface AsyncResult<T> {
  state: AsyncState<T>;
  /** Re-runs the fetcher. Safe to pass straight to a Retry button. */
  reload: () => void;
  /** Replaces the held value after a mutation, without a second round trip. */
  replace: (next: T) => void;
}

const LOADING = { status: 'loading', data: null, error: null } as const;

/**
 * Loads data once per key and reports failure honestly.
 *
 * Written deliberately without an automatic retry. The platform endpoints are
 * still being built, so a missing route is the normal case right now; retrying it
 * on a timer would turn "not deployed yet" into a request storm and an
 * apparently-hung screen. The caller gets the error and shows it.
 *
 * `key` is the only dependency. The fetcher lives in a ref so an inline arrow
 * function — which is a new value on every render — cannot re-trigger the effect
 * and spin forever, which is the classic way a dashboard like this melts down.
 */
export function useAsyncData<T>(key: string, fetcher: () => Promise<T>, enabled = true): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>(LOADING);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setState(LOADING);

    fetcherRef
      .current()
      .then((data) => {
        // A response that arrives after the key changed belongs to a screen the
        // user has already left; applying it would show the wrong institution.
        if (active) setState({ status: 'ready', data, error: null });
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setState({
          status: 'error',
          data: null,
          error:
            cause instanceof ApiError
              ? cause
              : new ApiError(cause instanceof Error ? cause.message : 'Request failed', 0, key),
        });
      });

    return () => {
      active = false;
    };
  }, [key, nonce, enabled]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const replace = useCallback((next: T) => setState({ status: 'ready', data: next, error: null }), []);

  return { state, reload, replace };
}
